import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import './SingleContentPage.scss';
import { Provider as CommunityContextProvider } from '../../../contexts/CommunityContext';
import { loadRightRailLayout } from '../../../utilities/loadPageContent';
import CommunityContentDisplay from '../../../components/Community/SingleContent/CommunityContentDisplay/CommunityContentDisplay';
import CommunityCommentsDisplay from '../../../components/Community/SingleContent/CommunityCommentsDisplay/CommunityCommentsDisplay';
import {
    singleTopicPropTypes,
    singleCommentPropTypes,
} from '../../defaultProps';
import SingleContentToFeedCTA from '../../../components/CallToAction/SingleContentToFeedCTA/SingleContentToFeedCTA';
import Header from '../../../components/Groups/Header/Header';
import ThreeColumnLayout from '../../../components/Layout/ThreeColumnLayout/ThreeColumnLayout';
import LeftRailContent from '../../../components/LeftRailLayout/LeftRailContent/LeftRailContent';
import MobileModalTrigger from '../../../components/Groups/Modals/MobileModalTrigger/MobileModalTrigger';
import GroupsRightRail from '../../../components/Groups/GroupsRightRail/GroupsRightRail';
import Layout from '../../../components/Layout/Layout';
import { PageContext } from '../../../contexts/ContextCreator';
import RightRailContent from '../../../components/Layout/ThreeColumnGridLayout/RightRailContent/RightRailContent';
import { SingleContentPageConditionalWrapper } from './SingleContentPageConditionalComponent';
import MobileWandTrigger from '../../../components/CallToAction/MobileWandTrigger/MobileWandTrigger';

export const PAGE_MODES = {
    COMMUNITY_CONTENT: 'community_content',
    COMMUNITY_COMMENT: 'community_comment',
};

const SingleContentPage = (props) => {
    const { modalTrigger } = useContext(PageContext);

    const newAdminOnboardingProps = {
        ...props.adminOnboardingProps,
        showBanner: true,
    };

    const profileLeftRailCTAMetrics = {
        LAT: 'user-left-rail',
        LAC: 'user-left-rail',
        clickMetric: 'user-left-rail-profile-cta-click',
        impressionMetric: 'user-left-rail-profile-cta-impression',
    };

    const isGroupContent = !!props?.content?.group;

    const ContentToShow = () => {
        if (props.pageMode === PAGE_MODES.COMMUNITY_COMMENT) {
            return (
                <CommunityCommentsDisplay
                    ancestorHash={props.ancestorHash}
                    content={props.content}
                />
            );
        }

        return (
            <CommunityContentDisplay
                content={props.content}
                hideReactionBar={props.hideReactionBar}
            />
        );
    };

    const Component = (
        <React.Fragment>
            {isGroupContent ? (
                <div className="group-header-width-container">
                    <Header useGenericLink hideGroupsCTA />
                </div>
            ) : null}

            <ThreeColumnLayout>
                <ThreeColumnLayout.LeftRail>
                    <LeftRailContent
                        allowProfileCTA
                        displayLeftRailFooter
                        profileLeftRailCTAMetrics={profileLeftRailCTAMetrics}
                    />
                </ThreeColumnLayout.LeftRail>
                <ThreeColumnLayout.MainContent>
                    <CommunityContextProvider
                        singleAttributionContentHash={props.content.hash}
                        attributionMode="single"
                        content={[props.content]}
                        topics={props.topics}
                    >
                        {ContentToShow()}
                        {!isGroupContent ? (
                            <SingleContentToFeedCTA />
                        ) : null}{' '}
                    </CommunityContextProvider>
                    {isGroupContent ? (
                        <MobileModalTrigger
                            modalTrigger={(modalData) =>
                                modalTrigger('GroupsMobileModal', {
                                    ...modalData,
                                    isReactRouterLink: false,
                                    onClose: () => modalTrigger(),
                                })
                            }
                        />
                    ) : (
                        <MobileWandTrigger location="single-content" />
                    )}
                </ThreeColumnLayout.MainContent>
                <ThreeColumnLayout.RightRail>
                    {isGroupContent ? (
                        <GroupsRightRail location={props.location} />
                    ) : (
                        <RightRailContent
                            contents={loadRightRailLayout('EditorialContent')}
                        />
                    )}
                </ThreeColumnLayout.RightRail>
            </ThreeColumnLayout>
        </React.Fragment>
    );

    return (
        <div className="page-Community-SingleContentPage">
            <Layout
                {...props}
                displayDesktopFooter
                topBannerOnlyShownList={['admin-onboard']}
                adminOnboardingProps={newAdminOnboardingProps}
                hasChangingContainer={false}
                fullScreenStyling
                hasBottomBanner={false}
                className={props.group?.groupUrl ? 'groups-layout' : ''}
            >
                <SingleContentPageConditionalWrapper
                    PAGE_PROPS={props.PAGE_PROPS}
                    userRelation={props.userRelation}
                    group={props.group}
                    render={() => Component}
                />
            </Layout>
        </div>
    );
};

// @TODO update this later with contract
SingleContentPage.propTypes = {
    PAGE_PROPS: PropTypes.shape({ featureFlags: {} }),
    /* eslint-disable-next-line  react/no-unused-prop-types */
    pageMode: PropTypes.oneOf([
        PAGE_MODES.COMMUNITY_CONTENT,
        PAGE_MODES.COMMUNITY_COMMENT,
    ]),
    /**
     * This is used in Three Column Grid Layout
     */
    topics: PropTypes.arrayOf(singleTopicPropTypes).isRequired,
    /**
     * This is used in Three Column Grid Layout
     */
    currentTopic: singleTopicPropTypes.isRequired,
    /**
     * Content props
     */
    content: PropTypes.shape({
        type: PropTypes.string,
        hash: PropTypes.string,
        lastId: PropTypes.number,
        ancestorHash: PropTypes.string,
        commentCount: PropTypes.number,
        comments: PropTypes.arrayOf(PropTypes.shape(singleCommentPropTypes)),
    }).isRequired,
    /**
     * Boolean as to hide reaction bar for the Editorial/Podcast Content
     */
    // This is a known es-lint bug where if it's a prop that's just passed, it's not considered used...
    /* eslint-disable-next-line  react/no-unused-prop-types */
    hideReactionBar: PropTypes.bool,
    group: PropTypes.shape({}),
    adminOnboardingProps: PropTypes.shape({}),
    location: PropTypes.string,
    userRelation: PropTypes.shape({}),
};

SingleContentPage.defaultProps = {
    PAGE_PROPS: {},
    pageMode: PAGE_MODES.COMMUNITY_CONTENT,
    hideReactionBar: false,
    adminOnboardingProps: null,
    group: null,
    location: null,
    userRelation: null,
};

export default React.memo(SingleContentPage);
